import React, { useCallback, useRef, useState } from 'react';
import { Button, message, Tooltip, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import Webcam from 'react-webcam';
import { CameraOutlined, PaperClipOutlined, RetweetOutlined } from '@ant-design/icons';
import { translate } from '../../services/i18n';
import { compressImages } from '../../utils/imageUtils';
import Modal from '../Modal/Modal';
import { QuestionType } from '../../services/prescription';

const PhotoUploader = ({
  type,
  onSkipPhotos,
  onFilesChange,
  initialFiles,
  uploadConfig,
}) => {
  const [ fileLists, setFileLists ] = useState(initialFiles);
  const [ visibleWebcams, setVisibleWebcams ] = useState({});
  const [ imageModal, setImageModal ] = useState(null);
  const [ isModalVisible, setIsModalVisible ] = useState(false);
  const [ facingMode, setFacingMode ] = useState('user');
  const webcamRefs = useRef({});

  const handleOnChange = async (e, index) => {
    const { file } = e;

    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      const isJpgOrPng = [ 'jpeg', 'png', 'jpg' ].includes(extension);

      if (!isJpgOrPng) {
        return message.error(translate('SHARED.fileValidation'));
      }

      file.thumbUrl = await compressImages([ file ]);

      const fileListsUpdated = [ ...fileLists ];
      fileListsUpdated[index] = [ { ...file, thumbUrl: file.thumbUrl, status: 'done', uid: '-1' } ];
      setFileLists(fileListsUpdated);
      onFilesChange(fileListsUpdated);
    } else {
      return message.error(translate('SHARED.fileValidation'));
    }
  };

  const capture = useCallback(async (index) => {
    if (webcamRefs.current[index]) {
      const imageSrc = webcamRefs.current[index].getScreenshot({ width: 500, height: 500 });

      if (imageSrc) {
        const fileListsUpdated = [ ...fileLists ];
        fileListsUpdated[index] = [ { uid: '-1', name: 'image.jpeg', status: 'done', thumbUrl: imageSrc } ];
        setFileLists(fileListsUpdated);
        onFilesChange(fileListsUpdated);
        toggleWebcam(index);
      }
    }
  }, [ fileLists, onFilesChange ]);

  const toggleWebcam = (index) => {
    setVisibleWebcams(a => ({ ...a, [index]: !a[index] }));
  };

  const toggleCamera = () => {
    setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
  };

  const onPreview = async (file) => {
    const src = file.url || file.thumbUrl;
    setImageModal(src);
    setIsModalVisible(true);
  };

  const WebcamComponent = ({ index }) => (
    <>
      {visibleWebcams[index] && (
        <Webcam
          audio={false}
          mirrored={true}
          height={300}
          ref={ref => (webcamRefs.current[index] = ref)}
          screenshotFormat="image/png"
          width={500}
          videoConstraints={{ width: 500, height: 500, facingMode }}
        />
      )}
    </>
  );

  const typeClassMap = {
    [QuestionType.MELATONINA]: 'melatonina',
    [QuestionType.HAIR_LOSS]: 'hair-loss',
    [QuestionType.WL]: 'weight-loss'
  };

  const typeClass = typeClassMap[type];

  return (
    <div className={`prescription-step__single__answers__picture--${typeClass}`}>
      <div className={`prescription-step__single__answers__picture--${typeClass}__title`}>
        <p className={`prescription-step__single__answers__picture--${typeClass}__title__text`}>
          Hora da foto!
        </p>
        <p className={`prescription-step__single__answers__picture--${typeClass}__title__text`}>
          Estamos quase lá, agora só faltam as suas fotos. Essa etapa é importante, pois o médico poderá analisar melhor o seu quadro.
        </p>
        <p className={`prescription-step__single__answers__picture--${typeClass}__title__text`}>
          Fique tranquilo, suas fotos são sigilosas e não serão publicadas em nenhum local.
        </p>
      </div>
      <div className={`prescription-step__single__answers__picture--${typeClass}__container`}>
        {uploadConfig.map((config, index) => (
          <div key={index}
            className={`prescription-step__single__answers__picture--${typeClass}__container__item`}>
            {visibleWebcams[index] &&
              <>
                <WebcamComponent index={index} />
                <Tooltip className='switch-cam-mobile'
                  title={`Alternar para câmera ${facingMode === 'user' ? 'traseira' : 'frontal'}`}>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<RetweetOutlined />}
                    onClick={toggleCamera}
                  />
                </Tooltip>
              </>
            }
            {!visibleWebcams[index] && (
              <ImgCrop rotate
                zoom
                modalTitle="Editar arquivo"
                modalOk="Utilizar foto"
                modalCancel="Cancelar"
                grid>
                <Upload
                  listType="picture-card"
                  fileList={fileLists[index]}
                  onPreview={onPreview}
                  customRequest={(ev) => handleOnChange(ev, index)}
                  accept="image/*"
                >
                  <div>
                    <PaperClipOutlined />
                    <div style={{ marginTop: 8 }}>{translate('COMPONENTS.CLINIC.PICTURE.UPLOAD')}</div>
                  </div>
                </Upload>
              </ImgCrop>
            )}
            <div className={`prescription-step__single__answers__picture--${typeClass}__container__label`}>
              {config.label}
            </div>
            <div className={`prescription-step__single__answers__picture--${typeClass}__container__button-cam`}>
              {visibleWebcams[index] && (
                <Button className="capture-cam"
                  onClick={() => capture(index)}>
                  <CameraOutlined style={{ marginRight: '8px' }} />
                  {translate('COMPONENTS.CLINIC.PICTURE.CAPTURE')}
                </Button>
              )}
              <Button className="capture-cam"
                onClick={() => toggleWebcam(index)}>
                {!visibleWebcams[index] ? (
                  <>
                    <CameraOutlined style={{ marginRight: '8px' }} />
                    {translate('COMPONENTS.CLINIC.PICTURE.CAM')}
                  </>
                ) : (
                  translate('COMPONENTS.PRESCRIPTION.BUTTONS.CANCEL')
                )}
              </Button>
              <Upload
                customRequest={(ev) => handleOnChange(ev, index)}
                accept="image/*"
                showUploadList={false}
              >
                <Button className="capture-cam">
                  <PaperClipOutlined />
                  {translate('COMPONENTS.CLINIC.PICTURE.UPLOAD')}
                </Button>
              </Upload>
            </div>
          </div>
        ))}
      </div>
      <div className='prescription-step__single__answers__picture__dont-send__div mt-2'>
        <a
          className='prescription-step__single__answers__picture__dont-send__text'
          onClick={onSkipPhotos}
        >
          {translate('COMPONENTS.CLINIC.PICTURE.SKIP')}
        </a>
      </div>
      {type === QuestionType.WL && (
        <div className="d-flex flex-column gap-8 prescription-step__single__answers__picture__desc mt-4">
          <p className="prescription-step__single__answers__picture--weight-loss__title__text">
            {translate('COMPONENTS.PRESCRIPTION.FINISH_WL.ONE')}
          </p>
          <p className="prescription-step__single__answers__picture--weight-loss__title__text">
            {translate('COMPONENTS.PRESCRIPTION.FINISH_WL.TWO')}
          </p>
          <p className="prescription-step__single__answers__picture--weight-loss__title__text">
            {translate('COMPONENTS.PRESCRIPTION.FINISH_WL.THREE')}
          </p>
        </div>
      )}
      <Modal
        isOpen={isModalVisible}
      >
        <img alt="preview"
          style={{ width: '100%' }}
          src={imageModal} />
        <Button className='mt-3'
          onClick={() => setIsModalVisible(false)}>
          Fechar
        </Button>
      </Modal>
    </div>
  );
};

export default PhotoUploader;
